// src/pages/Jobs.tsx - Job register with archive-ready controls, detail view, and create modal
import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_JOBS, MOCK_CLIENTS, PROJECT_TYPE_META } from '../utils/mockData'
import {
  ARCHIVE_OUTPUT_META,
  ARCHIVE_PROFILE_META,
  type ArchivePackageOutput,
  type ArchiveProfileId,
  type ArchiveRequest,
  type ArchiveRequestType,
  MOCK_ARCHIVE_REQUESTS,
  archiveReadinessMeta,
  getArchiveRequestsForJob,
  getJobArchiveStatus,
} from '../utils/mockDataArchive'
import { buildAndDownloadArchivePackage } from '../utils/archivePackage'
import { getBOMForJob, BOM_COMPLIANCE_META, BOM_STOCK_META, BOM_PROCUREMENT_META, BOM_SOURCE_META } from '../utils/canonicalBOM'
import { getJobMaterialsSummary, computeHandoverChecklist } from '../utils/workflowPipeline'
import { formatCurrency, formatDate, statusMeta } from '../utils/formatters'
import { MOCK_ITP } from '../utils/mockDataTier3'
import {
  Archive, ArrowLeft, Briefcase, Camera, CheckCircle, ChevronRight, ClipboardCheck,
  DollarSign, Download, FileText, MapPin, Package, Plus, Search, Truck, Users, X, AlertTriangle,
  Link2, Layers, Zap, Bot, Clock, ChevronDown, Lock, Phone, Shield,
} from 'lucide-react'
import {
  getCreatedJobs, getAwardEventForJob, getAgentActivityForJob,
  AGENT_AUTONOMY_META, AGENT_RESPONSE_META,
} from '../utils/mockDataProjectGaps'
import { MOCK_CONTRACTS as MOCK_PROJECT_CONTRACTS, MOCK_VARIATIONS, getVariationNoticeDaysLeft, getNoticeUrgency } from '../utils/mockDataCommercial'
import { MOCK_DOCUMENTS, DOC_TYPE_META, DOC_STATUS_META } from '../utils/mockDataDrawings'
import { useQuery } from '@tanstack/react-query'
import { fetchJobs } from '../services/jobsService'

type Job = typeof MOCK_JOBS[0]
type BOMLine = Job['bomLines'][0]

const BOM_STATUS: Record<string, { bg: string; text: string; label: string }> = {
  NOT_ORDERED: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Not ordered' },
  ON_ORDER: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'On order' },
  PARTIALLY_DELIVERED: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Part delivered' },
  DELIVERED: { bg: 'bg-green-100', text: 'text-green-700', label: 'Delivered' },
  INSTALLED: { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Installed' },
}

const ARCHIVE_REQUEST_STATUS_META: Record<ArchiveRequest['status'], { label: string; bg: string; text: string }> = {
  REQUESTED: { label: 'Requested', bg: 'bg-slate-100', text: 'text-slate-700' },
  APPROVED: { label: 'Approved', bg: 'bg-blue-100', text: 'text-blue-700' },
  QUEUED: { label: 'Queued', bg: 'bg-amber-100', text: 'text-amber-700' },
  GENERATING: { label: 'Generating', bg: 'bg-orange-100', text: 'text-orange-700' },
  READY: { label: 'Ready', bg: 'bg-green-100', text: 'text-green-700' },
}

const ARCHIVE_REQUEST_TYPE_META: Record<ArchiveRequestType, string> = {
  PROJECT_ARCHIVE: 'Project archive',
  MONTH_END_SNAPSHOT: 'Month-end snapshot',
  YEAR_END_SNAPSHOT: 'Year-end snapshot',
}

function profileForRequestType(requestType: ArchiveRequestType): ArchiveProfileId {
  if (requestType === 'PROJECT_ARCHIVE') return 'PROJECT_CLOSE_OUT'
  if (requestType === 'YEAR_END_SNAPSHOT') return 'YEAR_END_RECORDS'
  return 'MONTH_END_REPORTING'
}

function defaultTargetPeriodLabel(requestType: ArchiveRequestType) {
  if (requestType === 'PROJECT_ARCHIVE') return 'Project close-out'
  if (requestType === 'YEAR_END_SNAPSHOT') return 'FY2025-2026 year-end'
  return 'June 2026 month-end'
}

function CreateJobModal({ onClose, onCreate }: { onClose: () => void; onCreate: (job: Job) => void }) {
  const [form, setForm] = useState({
    jobNumber: `TP-2026-00${Math.floor(Math.random() * 90) + 10}`,
    description: '',
    type: 'SIGNALS',
    clientId: 'c1',
    startDate: new Date().toISOString().slice(0, 10),
    targetEnd: '',
    contractValue: '',
    contractType: 'LUMP_SUM',
    siteAddress: '',
  })
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onCreate({
      ...form,
      id: `j_new_${Date.now()}`,
      descriptionLong: '',
      status: 'NOT_STARTED',
      progressPercent: 0,
      crew: 0,
      plantOnSite: 0,
      openIssues: 0,
      openHoldPoints: 0,
      openNCRs: 0,
      contractValue: Number(form.contractValue),
      poCommitted: 0,
      clientName: MOCK_CLIENTS.find((c) => c.id === form.clientId)?.name ?? '',
      clientContact: '',
      pmId: 'pm_1',
      pmName: 'Jane PM',
      contractNumber: '',
      bomLines: [],
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Create new job</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Job number</label>
              <input className="input" value={form.jobNumber} onChange={(e) => set('jobNumber', e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Project type</label>
              <select className="input" value={form.type} onChange={(e) => set('type', e.target.value)}>
                <option value="SIGNALS">Traffic / Signals</option>
                <option value="CIVIL">Civil Electrical</option>
                <option value="ENERGY_GRID">Energy Grid / HV</option>
                <option value="MUNICIPAL">Municipal</option>
                <option value="ITS">ITS / Comms</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <input className="input" value={form.description} onChange={(e) => set('description', e.target.value)} required placeholder="Brief project description" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Client</label>
            <select className="input" value={form.clientId} onChange={(e) => set('clientId', e.target.value)}>
              {MOCK_CLIENTS.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Site address</label>
            <input className="input" value={form.siteAddress} onChange={(e) => set('siteAddress', e.target.value)} placeholder="Street address, suburb QLD postcode" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
              <input type="date" className="input" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Target completion</label>
              <input type="date" className="input" value={form.targetEnd} onChange={(e) => set('targetEnd', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Contract value (ex GST)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input type="number" className="input pl-7" value={form.contractValue} onChange={(e) => set('contractValue', e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Contract type</label>
              <select className="input" value={form.contractType} onChange={(e) => set('contractType', e.target.value)}>
                <option value="LUMP_SUM">Lump sum</option>
                <option value="AS2124">AS2124</option>
                <option value="AS4000">AS4000</option>
                <option value="NEC4">NEC4</option>
                <option value="PANEL_SOA">Panel / SOA</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Create job</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ArchiveProgressCard({ label, pct }: { label: string; pct: number }) {
  const readiness = archiveReadinessMeta(pct)
  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${readiness.bg} ${readiness.text}`}>{pct}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${pct >= 85 ? 'bg-green-500' : pct >= 65 ? 'bg-amber-500' : 'bg-slate-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs text-gray-400 mt-2">{readiness.label}</div>
    </div>
  )
}

function ArchiveRequestModal({
  job,
  mode,
  onClose,
  onCreate,
}: {
  job: Job
  mode: 'archive' | 'snapshot'
  onClose: () => void
  onCreate: (request: Omit<ArchiveRequest, 'id' | 'jobId' | 'status' | 'requestedAt' | 'requestedByName' | 'readyAt'>) => void
}) {
  const initialType: ArchiveRequestType = mode === 'archive' ? 'PROJECT_ARCHIVE' : 'MONTH_END_SNAPSHOT'
  const initialProfile = profileForRequestType(initialType)

  const [requestType, setRequestType] = useState<ArchiveRequestType>(initialType)
  const [profileId, setProfileId] = useState<ArchiveProfileId>(initialProfile)
  const [outputs, setOutputs] = useState<ArchivePackageOutput[]>(ARCHIVE_PROFILE_META[initialProfile].defaultOutputs)
  const [targetPeriodLabel, setTargetPeriodLabel] = useState(defaultTargetPeriodLabel(initialType))
  const [notes, setNotes] = useState('')

  const allowedTypes: ArchiveRequestType[] = mode === 'archive'
    ? ['PROJECT_ARCHIVE']
    : ['MONTH_END_SNAPSHOT', 'YEAR_END_SNAPSHOT']

  const handleRequestTypeChange = (nextType: ArchiveRequestType) => {
    const nextProfile = profileForRequestType(nextType)
    setRequestType(nextType)
    setProfileId(nextProfile)
    setOutputs(ARCHIVE_PROFILE_META[nextProfile].defaultOutputs)
    setTargetPeriodLabel(defaultTargetPeriodLabel(nextType))
  }

  const handleProfileChange = (nextProfile: ArchiveProfileId) => {
    setProfileId(nextProfile)
    setOutputs(ARCHIVE_PROFILE_META[nextProfile].defaultOutputs)
  }

  const toggleOutput = (output: ArchivePackageOutput) => {
    setOutputs((prev) => prev.includes(output)
      ? prev.filter((item) => item !== output)
      : [...prev, output]
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (outputs.length === 0) return
    onCreate({
      requestType,
      profileId,
      notes: notes || null,
      outputs,
      targetPeriodLabel,
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="font-semibold text-gray-900">
              {mode === 'archive' ? 'Request project archive' : 'Request reporting snapshot'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">{job.jobNumber} · {job.description}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Request type</label>
              <select className="input" value={requestType} onChange={(e) => handleRequestTypeChange(e.target.value as ArchiveRequestType)}>
                {allowedTypes.map((type) => (
                  <option key={type} value={type}>{ARCHIVE_REQUEST_TYPE_META[type]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Archive profile</label>
              <select className="input" value={profileId} onChange={(e) => handleProfileChange(e.target.value as ArchiveProfileId)}>
                {(Object.keys(ARCHIVE_PROFILE_META) as ArchiveProfileId[]).map((profileKey) => (
                  <option key={profileKey} value={profileKey}>{ARCHIVE_PROFILE_META[profileKey].label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Target period / label</label>
              <input className="input" value={targetPeriodLabel} onChange={(e) => setTargetPeriodLabel(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Purpose</label>
              <div className="input min-h-[42px] flex items-center text-sm text-gray-600">
                {ARCHIVE_PROFILE_META[profileId].intendedUse}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm font-medium text-gray-900 mb-1">{ARCHIVE_PROFILE_META[profileId].description}</div>
            <div className="text-xs text-gray-500">Outputs can be adjusted before final approval.</div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Requested outputs</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(Object.keys(ARCHIVE_OUTPUT_META) as ArchivePackageOutput[]).map((outputKey) => (
                <label key={outputKey} className="border border-gray-200 rounded-xl p-3 flex items-start gap-3 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={outputs.includes(outputKey)}
                    onChange={() => toggleOutput(outputKey)}
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{ARCHIVE_OUTPUT_META[outputKey].label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{ARCHIVE_OUTPUT_META[outputKey].description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes for approver / archive queue</label>
            <textarea
              className="input min-h-[110px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Example: freeze June month-end reporting set and QAQC evidence pack for audit trail."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" disabled={outputs.length === 0}>Submit request</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function BOMTab({ job }: { job: Job }) {
  const [localLines, setLocalLines] = useState(job.bomLines)
  const canonLines = getBOMForJob(job.id)
  const complianceIssues = canonLines.filter(l => !l.complianceVerifiedForThisJob || l.complianceFlag !== 'COMPLIANT')
  const notOrdered = canonLines.filter(l => l.procurementStatus === 'NOT_ORDERED' && l.materialType === 'BUILD_MATERIAL')
  const pooledLines = canonLines.filter(l => l.source === 'POOLED_IN' || l.source === 'RETURNED')
  const total = canonLines.reduce((s, l) => s + l.total, 0)

  const displayLines = canonLines.length > 0 ? canonLines : []

  if (displayLines.length === 0 && job.bomLines.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">No BOM lines yet</p>
        <p className="text-xs mt-1">Import drawings to AI-extract materials, or add lines manually.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {complianceIssues.length > 0 && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-red-800">
            <strong>{complianceIssues.length} line{complianceIssues.length !== 1 ? 's' : ''} with compliance issues</strong> — resolve before RFQ can be sent or materials used on site.
            {complianceIssues.filter(l => l.source === 'POOLED_IN').length > 0 && (
              <span className="block mt-1 text-xs">⚠ Includes pooled materials — must be re-verified against this job's client specs before use.</span>
            )}
          </div>
        </div>
      )}
      {notOrdered.length > 0 && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <div className="flex-1 text-sm text-amber-800">
            <strong>{notOrdered.length} build material{notOrdered.length !== 1 ? 's' : ''}</strong> not yet on a purchase order — {formatCurrency(notOrdered.reduce((s, l) => s + l.total, 0))}
          </div>
          <button className="btn-primary text-xs py-1.5">Raise RFQ / PO</button>
        </div>
      )}
      {pooledLines.length > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <span className="text-blue-700 font-bold text-base">↗</span>
          <p className="text-sm text-blue-800">
            <strong>{pooledLines.length} line{pooledLines.length !== 1 ? 's' : ''}</strong> transferred from another job's surplus.
            All pooled materials <strong>must be re-verified compliant</strong> with this job's client specs before use.
          </p>
        </div>
      )}
      <div className="overflow-x-auto border border-gray-200 rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-xs text-gray-600 border-b">
              <th className="text-left py-2.5 px-3 font-semibold">Code</th>
              <th className="text-left py-2.5 px-3 font-semibold">Description / Spec</th>
              <th className="text-left py-2.5 px-3 font-semibold">Source</th>
              <th className="text-left py-2.5 px-3 font-semibold">Compliance</th>
              <th className="text-right py-2.5 px-3 font-semibold">Qty</th>
              <th className="text-right py-2.5 px-3 font-semibold">Total</th>
              <th className="text-left py-2.5 px-3 font-semibold">Stock</th>
              <th className="text-left py-2.5 px-3 font-semibold">Procurement</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {displayLines.map(line => {
              const compMeta = BOM_COMPLIANCE_META[line.complianceFlag]
              const stockMeta = BOM_STOCK_META[line.stockStatus]
              const procMeta = BOM_PROCUREMENT_META[line.procurementStatus]
              const srcMeta = BOM_SOURCE_META[line.source]
              return (
                <tr key={line.id} className={`hover:bg-gray-50 text-xs ${!line.complianceVerifiedForThisJob ? 'bg-red-50/30' : ''}`}>
                  <td className="py-2.5 px-3 font-mono text-gray-500">{line.code}</td>
                  <td className="py-2.5 px-3 max-w-[200px]">
                    <div className="font-medium text-gray-900 leading-tight">{line.description}</div>
                    {line.specRef && <div className="text-gray-400 mt-0.5">{line.specRef}</div>}
                    {line.drawingTaskId && <div className="text-purple-500 mt-0.5">Task: {line.drawingTaskId}</div>}
                    {line.pooledFromJobId && <div className="text-blue-500 mt-0.5">Pooled from: {line.pooledFromJobId}</div>}
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`px-1.5 py-0.5 rounded-full font-medium ${srcMeta.bg} ${srcMeta.text}`}>{srcMeta.icon} {srcMeta.label}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`px-1.5 py-0.5 rounded-full font-medium border ${compMeta.bg} ${compMeta.text} ${compMeta.border}`}>{compMeta.label}</span>
                    {!line.complianceVerifiedForThisJob && <div className="text-red-600 mt-0.5 font-medium">⚠ Not verified</div>}
                    {line.complianceNote && <div className="text-gray-400 mt-0.5 max-w-[120px] truncate" title={line.complianceNote}>{line.complianceNote}</div>}
                  </td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{line.qty} {line.unit}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums font-medium">{formatCurrency(line.total)}</td>
                  <td className="py-2.5 px-3">
                    <span className={`px-1.5 py-0.5 rounded-full font-medium ${stockMeta.bg} ${stockMeta.text}`}>{stockMeta.label}</span>
                    {line.receivedQty !== undefined && line.receivedQty > 0 && line.receivedQty < line.qty && (
                      <div className="text-gray-400 mt-0.5">{line.receivedQty}/{line.qty} received</div>
                    )}
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`px-1.5 py-0.5 rounded-full font-medium ${procMeta.bg} ${procMeta.text}`}>{procMeta.label}</span>
                    {line.poNumber && <div className="text-gray-400 mt-0.5">{line.poNumber}</div>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t">
        <div className="flex items-center gap-2">
          <BOQImportButton onImport={newLines => setLocalLines(prev => [...prev, ...newLines])} />
          <button onClick={() => generateBOQActualComparison({ ...job, bomLines: localLines })} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg font-medium">
            BOQ vs actual report
          </button>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-500">{displayLines.length} lines</span>
          {complianceIssues.length > 0 && <span className="text-red-600 font-medium">{complianceIssues.length} compliance issues</span>}
          <span className="font-semibold text-gray-900">Total: {formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  )
}
function ArchiveTab({
  job,
  archiveRequests,
  onRequestArchive,
  onRequestSnapshot,
  onDownloadCurrentPackage,
  onDownloadArchiveRequest,
  exportFeedback,
}: {
  job: Job
  archiveRequests: ArchiveRequest[]
  onRequestArchive: () => void
  onRequestSnapshot: () => void
  onDownloadCurrentPackage: () => void
  onDownloadArchiveRequest: (request: ArchiveRequest) => void
  exportFeedback: string | null
}) {
  const archiveStatus = getJobArchiveStatus(job.id)
  const requestCount = archiveRequests.length
  const latestRequest = archiveRequests[0]

  if (!archiveStatus) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Archive className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">Archive model not set up for {job.jobNumber}</p>
      </div>
    )
  }

  const readiness = archiveReadinessMeta(archiveStatus.archiveReadyPct)

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-200 bg-slate-50 px-4 py-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${readiness.bg} ${readiness.text}`}>{readiness.label}</span>
            {archiveStatus.nextSnapshotDueDate && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                Next snapshot due {formatDate(archiveStatus.nextSnapshotDueDate)}
              </span>
            )}
            {requestCount > 0 && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                {requestCount} archive request{requestCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <h3 className="text-base font-semibold text-gray-900 mt-2">Archive readiness for {job.jobNumber}</h3>
          <p className="text-sm text-gray-600 mt-1">{archiveStatus.recommendedAction}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={onDownloadCurrentPackage} className="btn-secondary text-xs flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> Download current pack
          </button>
          <button onClick={onRequestSnapshot} className="btn-secondary text-xs flex items-center gap-1.5">
            <Camera className="w-3.5 h-3.5" /> Request snapshot
          </button>
          <button onClick={onRequestArchive} className="btn-primary text-xs flex items-center gap-1.5">
            <Archive className="w-3.5 h-3.5" /> Request archive
          </button>
        </div>
      </div>

      {exportFeedback && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {exportFeedback}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <ArchiveProgressCard label="Records coverage" pct={archiveStatus.recordsCoveragePct} />
        <ArchiveProgressCard label="QAQC coverage" pct={archiveStatus.qaqcCoveragePct} />
        <ArchiveProgressCard label="Commercial close" pct={archiveStatus.commercialClosePct} />
        <ArchiveProgressCard label="Handover close" pct={archiveStatus.handoverClosePct} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 space-y-5">
          <div className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardCheck className="w-4 h-4 text-brand-blue" />
              <h4 className="font-semibold text-gray-900">Blocking items</h4>
            </div>
            <div className="space-y-2">
              {archiveStatus.blockingItems.map((item) => (
                <div key={item} className="flex items-start gap-2 text-sm text-gray-700">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-brand-blue" />
              <h4 className="font-semibold text-gray-900">Available package outputs</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(Object.keys(ARCHIVE_OUTPUT_META) as ArchivePackageOutput[]).map((outputKey) => {
                const supported = outputKey === 'READ_ONLY_DASHBOARD'
                  ? archiveStatus.supportsReadOnlyDashboard
                  : outputKey === 'QAQC_ZIP'
                    ? archiveStatus.supportsQaqcZip
                    : true
                return (
                  <div key={outputKey} className="border border-gray-200 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-medium text-gray-900">{ARCHIVE_OUTPUT_META[outputKey].label}</div>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${supported ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {supported ? 'Supported' : 'Not ready'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{ARCHIVE_OUTPUT_META[outputKey].description}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="border border-gray-200 rounded-xl p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Archive dates</h4>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-gray-500">Last snapshot</div>
                <div className="font-medium text-gray-900 mt-0.5">{archiveStatus.lastSnapshotAt ? formatDate(archiveStatus.lastSnapshotAt) : 'Not generated yet'}</div>
              </div>
              <div>
                <div className="text-gray-500">Last archive</div>
                <div className="font-medium text-gray-900 mt-0.5">{archiveStatus.lastArchiveAt ? formatDate(archiveStatus.lastArchiveAt) : 'No archive pack yet'}</div>
              </div>
              <div>
                <div className="text-gray-500">Overall readiness</div>
                <div className="font-medium text-gray-900 mt-0.5">{archiveStatus.archiveReadyPct}%</div>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h4 className="font-semibold text-gray-900">Request queue</h4>
              {latestRequest && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ARCHIVE_REQUEST_STATUS_META[latestRequest.status].bg} ${ARCHIVE_REQUEST_STATUS_META[latestRequest.status].text}`}>
                  {ARCHIVE_REQUEST_STATUS_META[latestRequest.status].label}
                </span>
              )}
            </div>
            {archiveRequests.length === 0 ? (
              <p className="text-sm text-gray-500">No archive or snapshot requests submitted for this project.</p>
            ) : (
              <div className="space-y-3">
                {archiveRequests.map((request) => {
                  const status = ARCHIVE_REQUEST_STATUS_META[request.status]
                  return (
                    <div key={request.id} className="border border-gray-200 rounded-xl p-3">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <div className="text-sm font-medium text-gray-900">{ARCHIVE_REQUEST_TYPE_META[request.requestType]}</div>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${status.bg} ${status.text}`}>{status.label}</span>
                      </div>
                      <div className="text-xs text-gray-500">{request.targetPeriodLabel}</div>
                      <div className="text-xs text-gray-400 mt-1">Requested {formatDate(request.requestedAt)} by {request.requestedByName}</div>
                      <div className="text-xs text-gray-500 mt-2">{request.outputs.map((output) => ARCHIVE_OUTPUT_META[output].label).join(' · ')}</div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function JobDetail({
  job,
  onBack,
  archiveRequests,
  onCreateArchiveRequest,
  onDownloadArchivePackage,
  exportFeedback,
}: {
  job: Job
  onBack: () => void
  archiveRequests: ArchiveRequest[]
  onCreateArchiveRequest: (jobId: string, request: Omit<ArchiveRequest, 'id' | 'jobId' | 'status' | 'requestedAt' | 'requestedByName' | 'readyAt'>) => void
  onDownloadArchivePackage: (jobId: string, request?: ArchiveRequest) => void
  exportFeedback: string | null
}) {
  const [tab, setTab] = useState<'overview' | 'bom' | 'procurement' | 'activity' | 'archive' | 'drawings' | 'subcontractors' | 'agents'>('overview')
  const [showArchiveModalMode, setShowArchiveModalMode] = useState<null | 'archive' | 'snapshot'>(null)
  const meta = PROJECT_TYPE_META[job.type] ?? PROJECT_TYPE_META.SIGNALS
  const sm = statusMeta(job.status)
  const jobArchiveRequests = getArchiveRequestsForJob(job.id, archiveRequests)
  const jobDrawings = MOCK_DOCUMENTS.filter(d => d.jobId === job.id)
  const awardEvent = getAwardEventForJob(job.id)
  const agentActivity = getAgentActivityForJob(job.id)
  const tabs = [
    { key: 'overview',        label: 'Overview' },
    { key: 'bom',             label: `BOM & materials (${job.bomLines.length})` },
    { key: 'drawings',        label: `Drawings (${jobDrawings.length})` },
    { key: 'subcontractors',  label: 'Subcontractors' },
    { key: 'agents',          label: `Agent activity (${agentActivity.length})` },
    { key: 'procurement',     label: 'Procurement' },
    { key: 'activity',        label: 'Site activity' },
    { key: 'archive',         label: `Archive & snapshots (${jobArchiveRequests.length})` },
  ] as const

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> All jobs
      </button>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
        <div className="px-6 py-5 border-b">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-lg font-bold text-gray-900">{job.jobNumber}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${meta.bg} ${meta.color}`}>{meta.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
              </div>
              <h1 className="text-base font-medium text-gray-700">{job.description}</h1>
              {job.siteAddress && (
                <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                  <MapPin className="w-3.5 h-3.5" />{job.siteAddress}
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button className="btn-secondary text-xs">Edit job</button>
              <button onClick={() => setShowArchiveModalMode('snapshot')} className="btn-secondary text-xs flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5" /> Request snapshot
              </button>
              <button onClick={() => setShowArchiveModalMode('archive')} className="btn-secondary text-xs flex items-center gap-1.5">
                <Archive className="w-3.5 h-3.5" /> Request archive
              </button>
              <button onClick={() => onDownloadArchivePackage(job.id, jobArchiveRequests[0])} className="btn-primary text-xs flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Export pack
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 border-b">
          {[
            { label: 'Contract value', value: formatCurrency(job.contractValue) },
            { label: 'PO committed', value: formatCurrency(job.poCommitted) },
            { label: 'Crew on site', value: `${job.crew} people` },
            { label: 'Progress', value: `${job.progressPercent}%` },
          ].map((metric) => (
            <div key={metric.label} className="px-5 py-4">
              <div className="text-xs text-gray-500 mb-0.5">{metric.label}</div>
              <div className="font-bold text-gray-900 text-base">{metric.value}</div>
            </div>
          ))}
        </div>

        <div className="px-6 py-3 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-brand-blue rounded-full" style={{ width: `${job.progressPercent}%` }} />
            </div>
            <span className="text-xs text-gray-500 flex-shrink-0">
              {job.startDate && formatDate(job.startDate)} {'->'} {job.targetEnd ? formatDate(job.targetEnd) : 'TBC'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 flex-wrap">
        {tabs.map((item) => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            className={`flex-1 text-xs font-medium py-2 px-3 rounded-lg transition-colors ${
              tab === item.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        {tab === 'overview' && (
          <div className="space-y-5">
            {(awardEvent || (job as any)._fromTenderId) && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <Link2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-800">
                    Created from Tender {awardEvent?.tenderReference ?? (job as any)._fromTenderReference}
                  </p>
                  {awardEvent && (
                    <p className="text-xs text-green-700 mt-0.5">
                      {awardEvent.boqLineCount} BOQ lines imported as cost baseline
                      {awardEvent.contractNumber ? ` · Contract: ${awardEvent.contractNumber}` : ''}
                      {` · Awarded ${new Date(awardEvent.recordedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                      {` by ${awardEvent.recordedByName}`}
                    </p>
                  )}
                </div>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Award to Activate ✓</span>
              </div>
            )}
            {(() => {
              const projectContract = MOCK_PROJECT_CONTRACTS.find(c => c.jobId === job.id)
              if (!projectContract) return null
              const approvedVars   = MOCK_VARIATIONS.filter(v => v.jobId === job.id && v.status === 'APPROVED')
              const approvedVarTotal = approvedVars.reduce((s, v) => s + (v.approvedAmount ?? 0), 0)
              const runningSum     = projectContract.originalContractValue + approvedVarTotal
              const daysToPC       = Math.ceil((new Date(projectContract.practicalCompletionDate).getTime() - Date.now()) / 86400000)
              const bondDays       = projectContract.bondExpiry ? Math.ceil((new Date(projectContract.bondExpiry).getTime() - Date.now()) / 86400000) : null
              const urgentVarCount = MOCK_VARIATIONS.filter(v => v.contractId === projectContract.id && !v.noticeIssuedDate && v.status !== 'APPROVED' && ['URGENT','MISSED'].includes(getNoticeUrgency(getVariationNoticeDaysLeft(v)))).length
              const fmtA = (n: number) => new Intl.NumberFormat('en-AU',{style:'currency',currency:'AUD',minimumFractionDigits:0}).format(n)
              const fmtD = (iso: string) => new Date(iso).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'numeric'})
              return (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-[#1F3864] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-white">Contract Status</span>
                      <span className="text-[10px] text-blue-300 font-mono">{projectContract.contractNumber}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                        projectContract.contractType === 'AS4000' ? 'bg-indigo-900/50 text-indigo-300' :
                        projectContract.contractType === 'AS2124' ? 'bg-blue-900/50 text-blue-300' :
                        projectContract.contractType === 'NEC4'   ? 'bg-purple-900/50 text-purple-300' :
                        'bg-teal-900/50 text-teal-300'
                      }`}>{projectContract.contractType === 'PANEL_SOA' ? 'SOA Panel' : projectContract.contractType}</span>
                    </div>
                    {urgentVarCount > 0 && <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-bold">{urgentVarCount} notice{urgentVarCount !== 1 ? 's' : ''} urgent</span>}
                  </div>
                  <div className="grid grid-cols-3 divide-x text-xs">
                    {[
                      { label: 'Original value',   value: fmtA(projectContract.originalContractValue),  warn: false  },
                      { label: 'Running sum',       value: fmtA(runningSum),                              highlight: true },
                      { label: 'Retention held',    value: fmtA(projectContract.retentionHeld),           warn: false  },
                      { label: 'PC date',           value: `${fmtD(projectContract.practicalCompletionDate)}${daysToPC >= 0 && daysToPC <= 30 ? ` (${daysToPC}d)` : ''}`, warn: daysToPC >= 0 && daysToPC <= 30 },
                      { label: 'LD rate',           value: `${fmtA(projectContract.liquidatedDamagesRate)}/day`, warn: projectContract.liquidatedDamagesActive },
                      { label: 'Bond',              value: projectContract.bondRequired ? `${fmtA(projectContract.bondValue ?? 0)}${bondDays !== null && bondDays < 90 ? ` · ${bondDays}d` : ''}` : 'Not required', warn: bondDays !== null && bondDays < 90 },
                    ].map(({ label, value, warn, highlight }: any) => (
                      <div key={label} className={`px-3 py-2.5 ${highlight ? 'bg-blue-50' : warn ? 'bg-amber-50' : ''}`}>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">{label}</div>
                        <div className={`font-semibold ${ highlight ? 'text-[#1F3864]' : warn ? 'text-amber-700' : 'text-gray-900'}`}>{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}

            {(() => {
              const matSummary = getJobMaterialsSummary(job.id, job.jobNumber)
              const handoverChecklist = computeHandoverChecklist(job.id)
              const hcComplete = handoverChecklist.filter(c => c.status === 'COMPLETE').length
              const hcTotal = handoverChecklist.length
              const hcBlocked = handoverChecklist.filter(c => c.status === 'BLOCKED').length
              return (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Pipeline health</h3>
                    <span className="text-xs text-gray-400">Live — from field records</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Materials ready', value: matSummary.readyForWork, type: 'bool', yes: 'Ready for site work', no: `${matSummary.onSite}/${matSummary.totalLines} lines on site` },
                      { label: 'Compliance issues', value: matSummary.complianceIssues, type: 'count', warn: matSummary.complianceIssues > 0, suffix: ' BOM line(s)' },
                      { label: 'Handover checklist', value: `${hcComplete}/${hcTotal}`, type: 'text', warn: hcBlocked > 0, sub: hcBlocked > 0 ? `${hcBlocked} blocked` : hcComplete === hcTotal ? 'All complete' : 'In progress' },
                      { label: 'Pooled materials', value: matSummary.pooledLines, type: 'count', warn: false, suffix: ' transferred line(s)' },
                    ].map(c => (
                      <div key={c.label} className={`rounded-lg p-3 border ${c.warn ? 'bg-amber-50 border-amber-200' : c.type === 'bool' && c.value ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                        <div className="text-[10px] text-gray-500 mb-1">{c.label}</div>
                        {c.type === 'bool'
                          ? <div className={`text-xs font-semibold ${c.value ? 'text-green-700' : 'text-amber-700'}`}>{c.value ? '✓ ' + c.yes : '○ ' + c.no}</div>
                          : c.type === 'text'
                          ? <><div className={`text-sm font-bold ${c.warn ? 'text-amber-700' : 'text-gray-900'}`}>{String(c.value)}</div><div className="text-[10px] text-gray-400">{c.sub}</div></>
                          : <div className={`text-sm font-bold ${c.warn ? 'text-amber-700' : 'text-gray-900'}`}>{String(c.value)}{c.suffix}</div>}
                      </div>
                    ))}
                  </div>
                  {matSummary.complianceIssues > 0 && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      ⚠ {matSummary.complianceIssues} BOM line{matSummary.complianceIssues !== 1 ? 's' : ''} with compliance issues — check the BOM tab.
                    </p>
                  )}
                </div>
              )
            })()}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Scope of works</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{job.descriptionLong || job.description}</p>
            </div>

            {(() => {
              const itp = (MOCK_ITP as any).jobId === job.id ? MOCK_ITP as any : null
              if (!itp) return null
              const holdPoints = (itp.items as any[]).filter(i => i.inspectionType === 'HOLD_POINT')
              const released = holdPoints.filter(i => i.holdPointReleased).length
              const total = holdPoints.length
              const pct = total === 0 ? 100 : Math.round((released / total) * 100)
              const isReady = released === total && total > 0
              const openHPs = holdPoints.filter((i: any) => !i.holdPointReleased)
              return (
                <div className={`rounded-xl border p-4 space-y-2 ${
                  isReady ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Handover Readiness</h3>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      isReady ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>{pct}% {isReady ? '— Ready' : '— In progress'}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">ITP hold points released</span>
                    <span className={`font-semibold ${released === total ? 'text-green-700' : 'text-amber-700'}`}>{released}/{total}</span>
                  </div>
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${
                      isReady ? 'bg-green-500' : 'bg-amber-400'
                    }`} style={{ width: `${pct}%` }} />
                  </div>
                  {openHPs.length > 0 && openHPs.map((hp: any) => (
                    <div key={hp.id} className="flex items-center gap-1.5 text-xs text-red-700">
                      <Lock className="w-3 h-3 text-red-500 flex-shrink-0" />
                      <span>{hp.itemNumber} — {hp.activity}</span>
                    </div>
                  ))}
                  {isReady && (
                    <p className="text-xs text-green-700 font-medium">✓ All ITP hold points released — ready to initiate handover pack</p>
                  )}
                </div>
              )
            })()}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t text-sm">
              <div><span className="text-gray-500">Client</span><div className="font-medium mt-0.5">{job.clientName}</div></div>
              <div><span className="text-gray-500">Project manager</span><div className="font-medium mt-0.5">{job.pmName}</div></div>
              <div><span className="text-gray-500">Contract type</span><div className="font-medium mt-0.5">{job.contractType?.replace('_', ' ') ?? '-'}</div></div>
              <div><span className="text-gray-500">Contract number</span><div className="font-medium mt-0.5">{job.contractNumber || '-'}</div></div>
              <div><span className="text-gray-500">Open issues</span><div className={`font-medium mt-0.5 ${job.openIssues > 0 ? 'text-red-600' : 'text-green-600'}`}>{job.openIssues}</div></div>
              <div><span className="text-gray-500">Hold points</span><div className={`font-medium mt-0.5 ${job.openHoldPoints > 0 ? 'text-amber-600' : 'text-green-600'}`}>{job.openHoldPoints}</div></div>
            </div>
          </div>
        )}
        {tab === 'bom' && <BOMTab job={job} />}
        {tab === 'drawings' && (
          <DrawingsJobTab jobId={job.id} jobNumber={job.jobNumber} drawings={jobDrawings} />
        )}
        {tab === 'subcontractors' && (
          <SubcontractorsJobTab jobId={job.id} job={job} />
        )}
        {tab === 'agents' && (
          <AgentActivityJobTab activity={agentActivity} jobId={job.id} />
        )}
        {tab === 'procurement' && (
          <div className="text-center py-12 text-gray-400">
            <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Purchase orders for {job.jobNumber}</p>
            <p className="text-xs mt-1">Open the Purchase Orders module to manage POs for this job</p>
          </div>
        )}
        {tab === 'activity' && (
          <div className="text-center py-12 text-gray-400">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Site diary and activity for {job.jobNumber}</p>
            <p className="text-xs mt-1">Site diary and ITP modules coming in the next build step</p>
          </div>
        )}
        {tab === 'archive' && (
          <ArchiveTab
            job={job}
            archiveRequests={jobArchiveRequests}
            onRequestArchive={() => setShowArchiveModalMode('archive')}
            onRequestSnapshot={() => setShowArchiveModalMode('snapshot')}
            onDownloadCurrentPackage={() => onDownloadArchivePackage(job.id, jobArchiveRequests[0])}
            onDownloadArchiveRequest={(request) => onDownloadArchivePackage(job.id, request)}
            exportFeedback={exportFeedback}
          />
        )}
      </div>

      {showArchiveModalMode && (
        <ArchiveRequestModal
          job={job}
          mode={showArchiveModalMode}
          onClose={() => setShowArchiveModalMode(null)}
          onCreate={(request) => onCreateArchiveRequest(job.id, request)}
        />
      )}
    </div>
  )
}

// ─── GST formatting helpers ────────────────────────────────────────
function fmtX(n: number): string {
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
function gstRows(exGST: number, label = 'Amount'): string {
  const gst   = exGST * 0.1
  const total = exGST + gst
  return `
    <tr style="border-top:1px solid #e5e7eb">
      <td colspan="999" style="padding:2px"></td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">${label} (ex GST)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(exGST)}</td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">GST (10%)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(gst)}</td>
    </tr>
    <tr style="background:#f0f9ff">
      <td colspan="2" style="padding:6px 10px;text-align:right;font-weight:bold">Total (inc GST)</td>
      <td style="padding:6px 10px;text-align:right;font-weight:bold;font-family:monospace">${fmtX(total)}</td>
    </tr>`
}

function generateBOQActualComparison(job: any) {
  const now    = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const fmtAUD = fmtX
  const rows = job.bomLines.map((l: any, i: number) => {
    const budgeted = l.qty * l.unitCost
    const isActual = l.status === 'DELIVERED'
    const isCommitted = l.status === 'ON_ORDER'
    const actual = isActual ? budgeted : 0
    const committed = isCommitted ? budgeted : 0
    const statusColor = l.status === 'DELIVERED' ? '#166534' : l.status === 'ON_ORDER' ? '#1d4ed8' : '#b91c1c'
    return `<tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:6px 10px;font-family:monospace;font-size:9pt">${l.code}</td>
      <td style="padding:6px 10px;font-size:9.5pt">${l.description}</td>
      <td style="padding:6px 8px;text-align:center">${l.qty} ${l.unit}</td>
      <td style="padding:6px 8px;text-align:right">${fmtAUD(budgeted)}</td>
      <td style="padding:6px 8px;text-align:right;color:#166534;font-weight:bold">${isActual ? fmtAUD(actual) : '—'}</td>
      <td style="padding:6px 8px;text-align:right;color:#1d4ed8">${isCommitted ? fmtAUD(committed) : '—'}</td>
      <td style="padding:6px 8px;text-align:center;font-weight:bold;color:${statusColor}">${l.status.replace(/_/g,' ')}</td>
      <td style="padding:6px 8px;font-size:8.5pt;color:#888">${l.poNumber ?? '—'}</td>
    </tr>`
  }).join('')
  const totalBudgeted = job.bomLines.reduce((s: number, l: any) => s + l.qty * l.unitCost, 0)
  const totalActual = job.bomLines.filter((l: any) => l.status === 'DELIVERED').reduce((s: number, l: any) => s + l.qty * l.unitCost, 0)
  const totalCommitted = job.bomLines.filter((l: any) => l.status === 'ON_ORDER').reduce((s: number, l: any) => s + l.qty * l.unitCost, 0)
  const totalNotOrdered = job.bomLines.filter((l: any) => l.status === 'NOT_ORDERED').reduce((s: number, l: any) => s + l.qty * l.unitCost, 0)
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>BOQ vs Actual — ${job.jobNumber}</title>
<style>*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:14mm}h1{font-size:20pt;font-weight:900;color:#1B3A6B;margin:0}.hdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #1B3A6B;padding-bottom:10px;margin-bottom:12px}.kpi{display:flex;gap:10px;margin-bottom:12px}.kpi-box{flex:1;border:1px solid #e5e7eb;border-radius:5px;padding:8px;text-align:center}.kpi-v{font-size:15pt;font-weight:900}table{width:100%;border-collapse:collapse;font-size:9.5pt}th{background:#1B3A6B;color:#fff;padding:6px 8px;text-align:left}.total-row{background:#1B3A6B;color:#fff;font-weight:bold}.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:16px;border-top:1px solid #eee;padding-top:6px}@media print{body{margin:10mm}}</style></head><body>
<div class="hdr"><div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 12 345 678 901</div></div><div style="text-align:right"><div style="font-size:16pt;font-weight:900;color:#1B3A6B">BOQ VS ACTUAL COMPARISON</div><div style="font-size:9pt;color:#888">${job.jobNumber} · As at: ${now}</div></div></div>
<p style="font-size:11pt;font-weight:bold;color:#555;margin-bottom:10px">${job.description}</p>
<div class="kpi"><div class="kpi-box"><div class="kpi-v" style="color:#1B3A6B">${fmtAUD(totalBudgeted)}</div><div style="font-size:9pt;color:#6b7280">Total BOQ budget</div></div><div class="kpi-box"><div class="kpi-v" style="color:#166534">${fmtAUD(totalActual)}</div><div style="font-size:9pt;color:#6b7280">Delivered (actual)</div></div><div class="kpi-box"><div class="kpi-v" style="color:#1d4ed8">${fmtAUD(totalCommitted)}</div><div style="font-size:9pt;color:#6b7280">On order (committed)</div></div><div class="kpi-box"><div class="kpi-v" style="color:${totalNotOrdered>0?'#b91c1c':'#166534'}">${fmtAUD(totalNotOrdered)}</div><div style="font-size:9pt;color:#6b7280">Not yet ordered</div></div></div>
<table><thead><tr><th>Code</th><th>Description</th><th style="text-align:center">Qty</th><th style="text-align:right">Budgeted</th><th style="text-align:right">Actual (delivered)</th><th style="text-align:right">Committed (on order)</th><th style="text-align:center">Status</th><th>PO ref</th></tr></thead><tbody>${rows}<tr class="total-row"><td colspan="3" style="padding:7px 10px">TOTAL</td><td style="padding:7px 8px;text-align:right">${fmtAUD(totalBudgeted)}</td><td style="padding:7px 8px;text-align:right">${fmtAUD(totalActual)}</td><td style="padding:7px 8px;text-align:right">${fmtAUD(totalCommitted)}</td><td colspan="2"></td></tr></tbody></table>
<p style="font-size:8.5pt;color:#888;margin-top:8px">Budgeted reflects the bill of materials at planning unit costs. Actual reflects materials delivered to site. Committed reflects materials on order but not yet delivered.</p>
<div class="footer">MCB Civil Electrical · BOQ vs Actual Comparison · ${job.jobNumber} · ${now} · Confidential</div></body></html>`
  const w = window.open('', '_blank', 'width=1100,height=800')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

function downloadBOQTemplate() {
  const rows = [
    ['Code','Description','Material Type','Qty','Unit','Unit Cost'],
    ['CAB-PWR-6MM','6mm Twin + Earth Power Cable','BUILD_MATERIAL','250','m','4.80'],
    ['PPE-VEST-HV','High Vis Vest Class D/N','CONSUMABLE','8','EA','22.00'],
  ]
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join(String.fromCharCode(10))
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url; a.download = 'MCB_BOQ_Import_Template.csv'
  document.body.appendChild(a); a.click()
  document.body.removeChild(a); URL.revokeObjectURL(url)
}

function parseCSVLine(line: string): string[] {
  const out: string[] = []
  let cur = '', inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuotes && line[i+1] === '"') { cur += '"'; i++ }
      else inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      out.push(cur); cur = ''
    } else cur += ch
  }
  out.push(cur)
  return out.map(s => s.trim())
}

function parseBOQCSV(text: string): { lines: any[]; errors: string[] } {
  const rows = text.split(/\r?\n/).filter(r => r.trim().length > 0)
  if (rows.length < 2) return { lines: [], errors: ['File is empty or has no data rows'] }
  const header = parseCSVLine(rows[0]).map(h => h.toLowerCase())
  const idx = {
    code: header.indexOf('code'), desc: header.indexOf('description'),
    type: header.indexOf('material type'), qty: header.indexOf('qty'),
    unit: header.indexOf('unit'), cost: header.indexOf('unit cost'),
  }
  const errors: string[] = []
  const lines: any[] = []
  for (let i = 1; i < rows.length; i++) {
    const cells = parseCSVLine(rows[i])
    const code = cells[idx.code]?.trim()
    const desc = cells[idx.desc]?.trim()
    const qty = parseFloat(cells[idx.qty])
    const unitCost = parseFloat(cells[idx.cost])
    if (!code || !desc) { errors.push(`Row ${i+1}: missing code or description — skipped`); continue }
    if (isNaN(qty) || isNaN(unitCost)) { errors.push(`Row ${i+1}: invalid qty or unit cost — skipped`); continue }
    const materialType = (cells[idx.type]?.trim().toUpperCase() === 'CONSUMABLE') ? 'CONSUMABLE' : 'BUILD_MATERIAL'
    lines.push({
      id: `bom_imported_${Date.now()}_${i}`,
      code, description: desc, materialType,
      qty, unit: cells[idx.unit]?.trim() || 'EA',
      unitCost, total: qty * unitCost,
      status: 'NOT_ORDERED', poNumber: null,
    })
  }
  return { lines, errors }
}

function BOQImportButton({ onImport }: { onImport: (lines: any[]) => void }) {
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<{ count: number; errors: string[] } | null>(null)
  const inputRef = useState(() => Math.random().toString(36).slice(2))[0]

  const handleFile = (file: File) => {
    setImporting(true)
    const reader = new FileReader()
    reader.onload = e => {
      const text = String(e.target?.result ?? '')
      const { lines, errors } = parseBOQCSV(text)
      if (lines.length > 0) onImport(lines)
      setResult({ count: lines.length, errors })
      setImporting(false)
    }
    reader.onerror = () => { setResult({ count: 0, errors: ['Failed to read file'] }); setImporting(false) }
    reader.readAsText(file)
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button onClick={downloadBOQTemplate} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg font-medium">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
        Download BOQ template
      </button>
      <label className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-indigo-200 hover:bg-indigo-50 text-indigo-700 rounded-lg font-medium cursor-pointer">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
        {importing ? 'Importing…' : 'Import BOQ from CSV'}
        <input type="file" accept=".csv" className="hidden" disabled={importing}
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = '' }} />
      </label>
      {result && (
        <span className={`text-xs font-medium ${result.errors.length > 0 ? 'text-amber-600' : 'text-green-600'}`}>
          {result.count > 0 ? `✓ ${result.count} line${result.count!==1?'s':''} imported` : ''}
          {result.errors.length > 0 ? ` · ${result.errors.length} skipped` : ''}
        </span>
      )}
    </div>
  )
}

function DrawingsJobTab({ jobId, jobNumber, drawings }: { jobId: string; jobNumber: string; drawings: any[] }) {
  const [selected, setSelected] = useState<any | null>(null)
  const ifc = drawings.filter(d => d.status === 'APPROVED_ACTIVE')
  const pending = drawings.filter(d => d.status === 'DRAFT_PENDING_REVIEW')

  if (drawings.length === 0) {
    return (
      <div className="text-center py-14 text-gray-400">
        <Layers className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p className="text-sm font-medium">No drawings uploaded for {jobNumber} yet</p>
        <p className="text-xs mt-1">Upload drawings in the Drawings &amp; Specs module to see them here.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {[{ label: 'Total drawings', value: drawings.length, cls: 'text-gray-900' },
          { label: 'Approved / IFC', value: ifc.length, cls: 'text-green-700' },
          { label: 'Pending review', value: pending.length, cls: pending.length > 0 ? 'text-amber-600' : 'text-gray-400' },
        ].map(c => (
          <div key={c.label} className="border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-xl font-bold ${c.cls}`}>{c.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {drawings.map(doc => {
          const tm = DOC_TYPE_META[doc.docType as keyof typeof DOC_TYPE_META]
          const sm = DOC_STATUS_META[doc.status as keyof typeof DOC_STATUS_META]
          const isSelected = selected?.id === doc.id
          return (
            <div key={doc.id}
              className={`border rounded-xl p-4 transition-all cursor-pointer hover:shadow-sm ${isSelected ? 'border-blue-400 ring-2 ring-blue-100' : doc.status === 'DRAFT_PENDING_REVIEW' ? 'border-amber-200' : 'border-gray-200'}`}
              onClick={() => setSelected(isSelected ? null : doc)}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0 ${tm?.bg ?? 'bg-gray-100'}`}>
                  {tm?.icon ?? '📄'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-semibold text-gray-900 text-sm">{doc.title}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${tm?.bg ?? 'bg-gray-100'} ${tm?.text ?? 'text-gray-600'}`}>{tm?.label ?? doc.docType}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">Rev {doc.revisionNumber}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm?.bg ?? 'bg-gray-100'} ${sm?.text ?? 'text-gray-600'}`}>{sm?.label ?? doc.status}</span>
                    {doc.status === 'APPROVED_ACTIVE' && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">IFC</span>}
                    {doc.tasksExtracted > 0 && <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full"><Zap className="w-2.5 h-2.5 inline mr-0.5" />{doc.tasksExtracted} tasks</span>}
                  </div>
                  <p className="text-xs text-gray-500">{doc.pages ? `${doc.pages} pages · ` : ''}{doc.format} · {doc.uploadedByName}</p>
                  {doc.notes && <p className="text-xs text-gray-400 italic mt-0.5">"{doc.notes.slice(0,60)}"</p>}
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 mt-1 ${isSelected ? 'rotate-180' : ''}`} />
              </div>
              {isSelected && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  {doc.bomItemsExtracted > 0 && (
                    <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-1.5">
                      <Zap className="w-3 h-3 inline mr-1" />AI extracted {doc.tasksExtracted} tasks and {doc.bomItemsExtracted} BOM items from this drawing.
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" />Open in Drawings module
                    </button>
                    {doc.status === 'APPROVED_ACTIVE' && (
                      <span className="text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5">
                        <CheckCircle className="w-3.5 h-3.5" />Approved for construction (IFC)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
        <Zap className="w-3.5 h-3.5 inline mr-1 text-blue-600" />
        Drawing viewer with interactive WI and ITP pins is coming in Phase 2 of the gap-fill build.
        Upload and manage drawings in the <strong>Drawings &amp; Specs</strong> module.
      </div>
    </div>
  )
}

function SubcontractorsJobTab({ jobId, job }: { jobId: string; job: any }) {
  const subs: any[] = []

  if (subs.length === 0) {
    return (
      <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center">
        <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
        <p className="font-medium text-gray-500">No subcontractors linked to {job?.jobNumber ?? jobId}</p>
        <button className="mt-4 btn-secondary text-sm flex items-center gap-1.5 mx-auto">
          <Plus className="w-3.5 h-3.5" />Link subcontractor
        </button>
      </div>
    )
  }

  const activeCount = subs.filter(s => s.status === 'ACTIVE').length
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          <span className="font-semibold">{subs.length}</span> subcontractor{subs.length !== 1 ? 's' : ''} ·{' '}
          <span className="text-green-700 font-medium">{activeCount} active</span>
        </p>
        <button className="btn-secondary text-xs flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" />Link subcontractor
        </button>
      </div>
    </div>
  )
}

function AgentActivityJobTab({ activity, jobId }: { activity: any[]; jobId: string }) {
  if (activity.length === 0) {
    return (
      <div className="text-center py-14 text-gray-400">
        <Bot className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p className="text-sm font-medium">No agent activity recorded for this job yet</p>
        <p className="text-xs mt-1">
          A04 WHS Officer and A09 QA Specialist agents (Wave 1) are active and will log actions here
          as they process SWMS, drawings, ITPs, and incidents for this job.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Bot className="w-3.5 h-3.5 text-blue-500" />
        {activity.length} agent action{activity.length !== 1 ? 's' : ''} logged ·
        All outputs are advisory — human review retained
      </div>
      {activity.map(entry => {
        const autonomy = AGENT_AUTONOMY_META[entry.autonomyLevel as 1 | 2 | 3]
        const response = AGENT_RESPONSE_META[entry.humanResponse as keyof typeof AGENT_RESPONSE_META]
        return (
          <div key={entry.id} className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs font-bold text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded">{entry.agentCode}</span>
                  <span className="text-xs font-medium text-gray-700">{entry.agentName}</span>
                  <span className="text-xs text-gray-400">{entry.agentWave}</span>
                  {autonomy && <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${autonomy.bg} ${autonomy.text}`}>{autonomy.label}</span>}
                  {response && <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${response.bg} ${response.text}`}>{response.label}</span>}
                </div>
                <p className="text-sm font-medium text-gray-900">{entry.action}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-xs text-gray-400">{new Date(entry.timestamp).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</div>
                <div className="text-xs text-gray-400">{new Date(entry.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">{entry.detail}</p>
            {entry.linkedModule && (
              <div className="flex items-center gap-1.5 text-xs text-blue-600">
                <Zap className="w-3 h-3" />Linked to: {entry.linkedModule}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function Jobs() {
  const { user, isTier } = useAuthStore()
  const [selected, setSelected] = useState<Job | null>(null)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [showCreate, setShowCreate] = useState(false)
  const [localCreated, setLocalCreated] = useState<Job[]>([])
  const [archiveRequests, setArchiveRequests] = useState<ArchiveRequest[]>(MOCK_ARCHIVE_REQUESTS)
  const [exportFeedback, setExportFeedback] = useState<string | null>(null)

  // Live jobs from Neon via Cloudflare Worker API
  const { data: apiJobs = [], isLoading: jobsLoading, isError: jobsError } = useQuery({
    queryKey: ['jobs'],
    queryFn: fetchJobs,
    staleTime: 30_000,
  })

  // Merge: real API jobs first, then any mock jobs not already in DB, then locally created
  const apiJobNumbers = new Set(apiJobs.map((j: any) => j.jobNumber))
  const mockFallback = MOCK_JOBS.filter(j => !apiJobNumbers.has(j.jobNumber))
  const jobs: Job[] = [
    ...(apiJobs as unknown as Job[]),
    ...mockFallback,
    ...localCreated,
    ...getCreatedJobs(),
  ]

  const handleCreateArchiveRequest = (
    jobId: string,
    request: Omit<ArchiveRequest, 'id' | 'jobId' | 'status' | 'requestedAt' | 'requestedByName' | 'readyAt'>,
  ) => {
    setArchiveRequests((prev) => [
      {
        id: `ar_${Date.now()}`,
        jobId,
        status: 'REQUESTED',
        requestedAt: new Date().toISOString(),
        requestedByName: user?.fullName ?? 'Current user',
        readyAt: null,
        ...request,
      },
      ...prev,
    ])
  }

  const handleDownloadArchivePackage = (jobId: string, request?: ArchiveRequest) => {
    try {
      const result = buildAndDownloadArchivePackage(jobId, request)
      setExportFeedback(`Local archive package ready: ${result.filename}. It is now logged in Archive centre. Unzip and open dashboard/index.html for the offline read-only view.`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Archive package generation failed.'
      setExportFeedback(message)
    }
  }

  if (selected) {
    return (
      <JobDetail
        job={selected}
        onBack={() => setSelected(null)}
        archiveRequests={archiveRequests}
        onCreateArchiveRequest={handleCreateArchiveRequest}
        onDownloadArchivePackage={handleDownloadArchivePackage}
        exportFeedback={exportFeedback}
      />
    )
  }

  const filtered = jobs.filter((job) => {
    const matchSearch = !search
      || job.jobNumber.toLowerCase().includes(search.toLowerCase())
      || job.description.toLowerCase().includes(search.toLowerCase())
      || job.clientName.toLowerCase().includes(search.toLowerCase())
    const matchType = typeFilter === 'ALL' || job.type === typeFilter
    const matchStatus = statusFilter === 'ALL' || job.status === statusFilter
    return matchSearch && matchType && matchStatus
  })

  const totalValue = filtered.reduce((s, job) => s + job.contractValue, 0)

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jobs & Sites</h1>
          <p className="text-sm text-gray-500 mt-0.5">{filtered.length} jobs · total value {formatCurrency(totalValue)}</p>
        </div>
        {isTier('B_PM') && (
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> New job
          </button>
        )}
      </div>

      {jobsLoading && (
        <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
          <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          Loading live jobs from Neon...
        </div>
      )}
      {jobsError && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          ⚠ API unreachable — showing mock data only. Check VITE_API_URL in your .env.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-xs text-gray-500 mb-1">Projects with snapshot due</div>
          <div className="text-2xl font-bold text-gray-900">
            {jobs.filter((job) => getJobArchiveStatus(job.id)?.nextSnapshotDueDate === '2026-06-30').length}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-xs text-gray-500 mb-1">Archive-ready projects</div>
          <div className="text-2xl font-bold text-gray-900">
            {jobs.filter((job) => (getJobArchiveStatus(job.id)?.archiveReadyPct ?? 0) >= 80).length}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-xs text-gray-500 mb-1">Open archive requests</div>
          <div className="text-2xl font-bold text-gray-900">
            {archiveRequests.filter((request) => ['REQUESTED', 'APPROVED', 'QUEUED', 'GENERATING'].includes(request.status)).length}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-xs text-gray-500 mb-1">Read-only archive capable</div>
          <div className="text-2xl font-bold text-gray-900">
            {jobs.filter((job) => getJobArchiveStatus(job.id)?.supportsReadOnlyDashboard).length}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9" placeholder="Search jobs, clients..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {['ALL', 'SIGNALS', 'CIVIL', 'ENERGY_GRID', 'MUNICIPAL', 'ITS'].map((filter) => (
            <button
              key={filter}
              onClick={() => setTypeFilter(filter)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${typeFilter === filter ? 'bg-brand-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {filter === 'ALL' ? 'All types' : PROJECT_TYPE_META[filter]?.label ?? filter}
            </button>
          ))}
        </div>
        <select className="input text-sm w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">All statuses</option>
          <option value="NOT_STARTED">Not started</option>
          <option value="PRE_START">Pre-start</option>
          <option value="IN_PROGRESS">In progress</option>
          <option value="PROCUREMENT">Procurement</option>
          <option value="COMPLETED">Completed</option>
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="divide-y divide-gray-100">
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <Briefcase className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No jobs match your search</p>
            </div>
          ) : filtered.map((job) => {
            const meta = PROJECT_TYPE_META[job.type] ?? PROJECT_TYPE_META.SIGNALS
            const sm = statusMeta(job.status)
            const bomNotOrdered = job.bomLines.filter((line) => line.status === 'NOT_ORDERED').length
            const archiveStatus = getJobArchiveStatus(job.id)
            const requestCount = getArchiveRequestsForJob(job.id, archiveRequests)
              .filter((request) => ['REQUESTED', 'APPROVED', 'QUEUED', 'GENERATING'].includes(request.status))
              .length
            const readiness = archiveStatus ? archiveReadinessMeta(archiveStatus.archiveReadyPct) : null

            return (
              <div
                key={job.id}
                onClick={() => setSelected(job)}
                className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 cursor-pointer group transition-colors"
              >
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${meta.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-semibold text-gray-900">{job.jobNumber}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${meta.bg} ${meta.color}`}>{meta.label}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                    {job.openIssues > 0 && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{job.openIssues} issue{job.openIssues !== 1 ? 's' : ''}</span>}
                    {bomNotOrdered > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{bomNotOrdered} BOM not ordered</span>}
                    {archiveStatus?.nextSnapshotDueDate === '2026-06-30' && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Snapshot due</span>
                    )}
                    {readiness && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${readiness.bg} ${readiness.text}`}>{readiness.label}</span>
                    )}
                    {requestCount > 0 && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{requestCount} archive queued</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{job.description}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {job.clientName ? `${job.clientName} · ${job.pmName}` : job.siteAddress || ''}
                  </p>
                </div>
                <div className="hidden sm:flex flex-col items-end gap-1 w-28 flex-shrink-0">
                  <span className="text-xs text-gray-500">{job.progressPercent}%</span>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-blue rounded-full" style={{ width: `${job.progressPercent}%` }} />
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-3 text-xs text-gray-400 flex-shrink-0">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{job.crew}</span>
                  <span className="flex items-center gap-1"><Truck className="w-3 h-3" />{job.plantOnSite}</span>
                </div>
                <div className="hidden lg:block text-right flex-shrink-0 w-28">
                  <div className="text-sm font-medium text-gray-900">{formatCurrency(job.contractValue)}</div>
                  <div className="text-xs text-gray-400">{formatCurrency(job.poCommitted)} committed</div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
              </div>
            )
          })}
        </div>
      </div>

      {showCreate && (
        <CreateJobModal
          onClose={() => setShowCreate(false)}
          onCreate={(job) => setLocalCreated((prev) => [job, ...prev])}
        />
      )}
    </div>
  )
}
